/**
=========================================================
* Argon Dashboard 2 MUI - v3.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-material-ui
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components

import Card from "@mui/material/Card";
import { ConfirmationNumber, Close as CloseIcon } from "@mui/icons-material";
import IconButton from "@mui/material/IconButton";

// Argon Dashboard 2 MUI components
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonButton from "components/ArgonButton";

// Argon Dashboard 2 MUI examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import Grid from "@mui/material/Grid";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  Select,
  MenuItem,
  Divider,
} from "@mui/material";
// Data
import ClassMgtTableData from "Views/Activities/ClassMgt/data/classMgtTableData";
import { useState, useEffect, useMemo } from "react";
import ApnaTabs from "components/ApnaTabs/ApnaTabs";
import ArgonInput from "components/ArgonInput";
import Dropdown from "components/CustomDropdown/Dropdown";
import DeleteDialog from "components/DeleteDialog/DeleteDialog";
import PaginationBar from "components/PaginationBar/PaginationBar";
import axios from "utils/api";
import { CircularProgress } from "@mui/material";
import { toast } from "react-toastify";
import { Person, Settings, Payment } from "@mui/icons-material";

// Toggle/comment this rule if 5-days-a-week classes should use the old 20-session default again.
const ENABLE_FIVE_DAY_WEEK_21_SESSION_DEFAULT = true;
const DEFAULT_CLASS_SESSION_COUNT = 20;
const FIVE_DAY_WEEK_CLASS_SESSION_COUNT = 21;

const resolveDefaultTotalClasses = (currentTotalClasses, classesPerWeek) => {
  const totalClassesNum = Number(currentTotalClasses || 0);
  const weeklyClassesNum = Number(classesPerWeek || 0);

  if (
    ENABLE_FIVE_DAY_WEEK_21_SESSION_DEFAULT &&
    weeklyClassesNum === 5 &&
    (!totalClassesNum || totalClassesNum === DEFAULT_CLASS_SESSION_COUNT)
  ) {
    return FIVE_DAY_WEEK_CLASS_SESSION_COUNT;
  }

  if (weeklyClassesNum !== 5 && totalClassesNum === FIVE_DAY_WEEK_CLASS_SESSION_COUNT) {
    return DEFAULT_CLASS_SESSION_COUNT;
  }

  return totalClassesNum || DEFAULT_CLASS_SESSION_COUNT;
};

const resolveMonthlySessionCount = ({ totalClasses, classesPerWeek }) => {
  const normalizedTotalClasses = Number(totalClasses || 0);
  const normalizedClassesPerWeek = Number(classesPerWeek || 0);

  if (normalizedTotalClasses > 0) {
    return normalizedTotalClasses;
  }

  return normalizedClassesPerWeek * 4;
};

const resolvePackageSessionCount = ({ totalClasses, noOfMonths, classesPerWeek }) => {
  const normalizedNoOfMonths = Number(noOfMonths || 0);
  const monthlySessions = resolveMonthlySessionCount({
    totalClasses,
    classesPerWeek,
  });

  return monthlySessions * (normalizedNoOfMonths > 0 ? normalizedNoOfMonths : 1);
};

const computeGrossMarginPercentage = ({ parentFee, tutorFeePerSession, monthlySessions }) => {
  const normalizedParentFee = Number(parentFee || 0);
  const normalizedTutorFeePerSession = Number(tutorFeePerSession || 0);
  const normalizedMonthlySessions = Number(monthlySessions || 0);

  if (normalizedParentFee <= 0) {
    return 0;
  }

  // Parents fee = (tutor fee per session x no. of monthly sessions) / (1 - margin)
  // Rearranged: margin % = (1 - tutor monthly payout / parents fee) x 100
  const tutorMonthlyPayout = normalizedTutorFeePerSession * normalizedMonthlySessions;
  return (1 - tutorMonthlyPayout / normalizedParentFee) * 100;
};

function Tables() {
  //popup
  const [deleteDialog, setDeleteDialog] = useState(false);

  const [selectedTutorDoc, setSelectedTutorDoc] = useState(null);
  const [tutorSalaryTouched, setTutorSalaryTouched] = useState(false);

  // Delete button click handler
  const handleDeleteClick = () => {
    setDeleteDialog(true);
  };

  // Close dialog
  const handleCloseDialog = () => {
    setDeleteDialog(false);
  };

  // State management
  const [classData, setClassData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [counts, setCounts] = useState({
    all: 0,
    active: 0,
    hold: 0,
    completed: 0,
    discontinued: 0,
    sme: 0,
    combined: 0,
    separate: 0,
  });

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [optionLoading, setOptionLoading] = useState(false);
  const [parentOptions, setParentOptions] = useState([]);
  const [studentOptions, setStudentOptions] = useState([]);
  const [tutorOptions, setTutorOptions] = useState([]);
  const [slotOptions, setSlotOptions] = useState([]);
  const [slotLoading, setSlotLoading] = useState(false);
  const [busySlotIds, setBusySlotIds] = useState([]);
  const [preferredSlotIds, setPreferredSlotIds] = useState([]);

  const initialCreateForm = {
    parentId: "",
    students: [],
    tutorId: "",
    meetingLink: "",
    finalisedActiveSlot: "",
    mode: "OFFLINE",
    classType: "NORMAL",
    totalNoOfClasses: 20,
    numberOfClassesInAWeek: 3, 
    paymentAmount: "",
    paymentMethod: "CASH",
    perSessionFee: "",
    tutorSalary: "",
    grossMargin: 0,
    monthlyFee: "",
    noOfMonths: 1,
    completedCycles: 0,
    duration: 60,
    address: "",
    scheduleDate: "",
    status: "CREATED",
  };

  const [createForm, setCreateForm] = useState(initialCreateForm);

  // Fetch classes from API
  useEffect(() => {
    fetchNormalClasses();
  }, []);

  useEffect(() => {
    if (!createDialogOpen) {
      return;
    }

    const fetchCreateOptions = async () => {
      try {
        setOptionLoading(true);
        const [parentsRes, tutorsRes] = await Promise.all([
          axios.get("/parent", {
            params: {
              page: 1,
              limit: 200,
            },
          }),
          axios.get("/tutor", {
            params: {
              approvalStatus: "APPROVED",
              page: 1,
              limit: 100,
            },
          }),
        ]);

        setParentOptions(parentsRes?.data?.data || []);
        setTutorOptions(
          Array.isArray(tutorsRes?.data?.data?.tutors)
            ? tutorsRes.data.data.tutors
            : Array.isArray(tutorsRes?.data?.tutors)
            ? tutorsRes.data.tutors
            : []
        );
      } catch (error) {
        console.error("Error fetching create class options:", error);
        toast.error("Failed to load options");
      } finally {
        setOptionLoading(false);
      }
    };

    fetchCreateOptions();
  }, [createDialogOpen]);

  useEffect(() => {
    if (!createForm.parentId) {
      setStudentOptions([]);
      return;
    }

    const fetchStudentsByParent = async () => {
      try {
        setOptionLoading(true);
        const studentsRes = await axios.get("/student", {
          params: {
            parentId: createForm.parentId,
            page: 1,
            limit: 200,
          },
        });

        setStudentOptions(studentsRes?.data?.data || []);
      } catch (error) {
        console.error("Error fetching students by parent:", error);
        setStudentOptions([]);
        toast.error("Failed to load students for selected parent");
      } finally {
        setOptionLoading(false);
      }
    };

    fetchStudentsByParent();
  }, [createForm.parentId]);

  useEffect(() => {
    const studentCount = Array.isArray(createForm.students) ? createForm.students.length : 0;
    const nextClassType = studentCount > 1 ? "GROUP" : "NORMAL";

    setCreateForm((prev) => {
      if (prev.classType === nextClassType) {
        return prev;
      }
      return {
        ...prev,
        classType: nextClassType,
      };
    });
  }, [createForm.students]);

  useEffect(() => {
    if (!createDialogOpen || !Array.isArray(createForm.students) || createForm.students.length === 0) {
      return;
    }

    const firstSelectedStudent = studentOptions.find(
      (student) => student?._id === createForm.students[0]
    );

    if (!firstSelectedStudent) {
      return;
    }

    const preferredDuration = Number(firstSelectedStudent.preferredTimeDuration || 0);
    const preferredClassesPerWeek = Number(firstSelectedStudent.noOfClassesPerWeek || 0);

    setCreateForm((prev) => {
      const nextForm = { ...prev };
      let changed = false;

      if (preferredDuration > 0 && Number(prev.duration || 0) !== preferredDuration) {
        nextForm.duration = preferredDuration;
        changed = true;
      }

      if (
        preferredClassesPerWeek > 0 &&
        Number(prev.numberOfClassesInAWeek || 0) !== preferredClassesPerWeek
      ) {
        nextForm.numberOfClassesInAWeek = preferredClassesPerWeek;
        nextForm.totalNoOfClasses = resolveDefaultTotalClasses(
          prev.totalNoOfClasses,
          preferredClassesPerWeek
        );
        changed = true;
      }

      return changed ? nextForm : prev;
    });
  }, [createDialogOpen, createForm.students, studentOptions]);

  const getSlotId = (slot) => {
    if (!slot) return "";
    if (typeof slot === "string") return slot;
    return slot._id || slot.id || "";
  };

  const getSlotLabel = (slot) => {
    if (!slot) return "";
    const start = slot.startTime || slot.start || "";
    const end = slot.endTime || slot.end || "";
    if (start && end) return `${start} - ${end}`;
    return start || end || "";
  };

  const normalizeSlots = (slotsRes) => {
    if (Array.isArray(slotsRes?.data?.data?.slots)) return slotsRes.data.data.slots;
    if (Array.isArray(slotsRes?.data?.slots)) return slotsRes.data.slots;
    if (Array.isArray(slotsRes?.data?.data)) return slotsRes.data.data;
    if (Array.isArray(slotsRes?.data)) return slotsRes.data;
    return [];
  };

  const extractAreaFromAddress = (address) => {
    if (!address) return "";
    const areaMatch = String(address).match(/Area:\s*([^,]+)/);
    return areaMatch ? areaMatch[1].trim() : "";
  };

  const normalizeLatLng = (value1, value2) => {
    const val1 = Number(value1);
    const val2 = Number(value2);
    if (!Number.isFinite(val1) || !Number.isFinite(val2)) return null;

    const val1Lat = Math.abs(val1) <= 90;
    const val1Lng = Math.abs(val1) <= 180;
    const val2Lat = Math.abs(val2) <= 90;
    const val2Lng = Math.abs(val2) <= 180;

    if (val1Lat && val2Lng) return { lat: val1, lng: val2 };
    if (val2Lat && val1Lng) return { lat: val2, lng: val1 };
    return null;
  };

  const getLocationCoords = (location) => {
    if (!location) return null;
    if (Array.isArray(location.coordinates) && location.coordinates.length === 2) {
      return normalizeLatLng(location.coordinates[0], location.coordinates[1]);
    }
    if (Number.isFinite(location.lat) && Number.isFinite(location.lng)) {
      return { lat: Number(location.lat), lng: Number(location.lng) };
    }
    return null;
  };

  const toRadians = (value) => (Number(value) * Math.PI) / 180;

  const getDistanceKm = (coordsA, coordsB) => {
    if (!coordsA || !coordsB) return null;
    const earthRadius = 6371;
    const dLat = toRadians(coordsB.lat - coordsA.lat);
    const dLng = toRadians(coordsB.lng - coordsA.lng);
    const lat1 = toRadians(coordsA.lat);
    const lat2 = toRadians(coordsB.lat);

    const sinLat = Math.sin(dLat / 2);
    const sinLng = Math.sin(dLng / 2);
    const a = sinLat * sinLat + Math.cos(lat1) * Math.cos(lat2) * sinLng * sinLng;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return earthRadius * c;
  };

  const getTutorArea = (tutor) => {
    if (!tutor) return "";
    return (
      tutor.area ||
      extractAreaFromAddress(tutor.address) ||
      extractAreaFromAddress(tutor.generatedMapAddress) ||
      extractAreaFromAddress(tutor.currentAddress) ||
      extractAreaFromAddress(tutor.permanentAddress)
    );
  };

  const formatTutorLabel = (tutor, distanceKm) => {
    const name = tutor?.fullName || tutor?.name || "Unnamed Tutor";
    const distanceLabel = Number.isFinite(distanceKm) ? `${distanceKm.toFixed(1)} km` : "N/A";

    return `${name} • ${distanceLabel}`;
  };

  const normalizeLevelLabel = (value) =>
    String(value || "")
      .trim()
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, "");

  const mapStudentClassToPricingLevel = (value) => {
    const normalized = normalizeLevelLabel(value);
    if (!normalized) return "";

    if (
      ["KG", "KINDERGARTEN", "NURSERY", "PREPRIMARY", "PRENURSERY", "LKG", "UKG"].includes(
        normalized
      )
    ) {
      return "KG";
    }

    if (["PRIMARY", "MIDDLE", "SECONDARY", "HIGHER"].includes(normalized)) {
      return normalized;
    }

    const classNumMatch = normalized.match(/(?:CLASS|STD|GRADE)?(\d{1,2})/);
    const classNum = Number(classNumMatch?.[1]);
    if (Number.isFinite(classNum)) {
      if (classNum <= 5) return "PRIMARY";
      if (classNum <= 8) return "MIDDLE";
      if (classNum <= 10) return "SECONDARY";
      if (classNum <= 12) return "HIGHER";
    }

    return "";
  };

  const getStudentEducationLevel = (student) =>
    student?.educationLevel || student?.education_level || student?.education || "";

  const getStudentLevelForTutorPricing = (student) => {
    const directEducationLevel = mapStudentClassToPricingLevel(getStudentEducationLevel(student));
    if (directEducationLevel) return directEducationLevel;

    const studentClassLabel = student?.class?.name || student?.className || student?.class || "";
    return mapStudentClassToPricingLevel(studentClassLabel);
  };

  const sortedTutorOptions = useMemo(() => {
    const parent = parentOptions.find((item) => item?._id === createForm.parentId);
    const parentCoords = getLocationCoords(parent?.location);

    const selectedStudentIds = Array.isArray(createForm.students) ? createForm.students : [];
    const selectedLevels = new Set(
      selectedStudentIds
        .map((studentId) => studentOptions.find((student) => student?._id === studentId))
        .map((student) => getStudentLevelForTutorPricing(student))
        .filter(Boolean)
    );

    const enrichedTutors = tutorOptions.map((tutor) => {
      const tutorCoords = getLocationCoords(tutor?.location);
      const distanceKm = parentCoords ? getDistanceKm(parentCoords, tutorCoords) : null;
      return {
        ...tutor,
        distanceKm,
      };
    });

    const filteredTutors = enrichedTutors.filter((tutor) => {
      if (selectedLevels.size === 0) return true;

      const tutorLevels = Array.isArray(tutor?.levelBasedPricing)
        ? tutor.levelBasedPricing
            .map((pricing) => mapStudentClassToPricingLevel(pricing?.level))
            .filter(Boolean)
        : [];

      if (tutorLevels.length === 0) {
        return false;
      }

      return tutorLevels.some((level) => selectedLevels.has(level));
    });

    if (!parentCoords) {
      return filteredTutors;
    }

    return [...filteredTutors].sort((a, b) => {
      const distA = Number.isFinite(a.distanceKm) ? a.distanceKm : Number.POSITIVE_INFINITY;
      const distB = Number.isFinite(b.distanceKm) ? b.distanceKm : Number.POSITIVE_INFINITY;
      return distA - distB;
    });
  }, [tutorOptions, parentOptions, studentOptions, createForm.parentId, createForm.students]);

  useEffect(() => {
    if (!createForm.tutorId) {
      return;
    }

    const selectedTutorStillVisible = sortedTutorOptions.some((tutor) => {
      const tutorId = tutor?.id || tutor?._id;
      return String(tutorId) === String(createForm.tutorId);
    });

    if (selectedTutorStillVisible) {
      return;
    }

    setSelectedTutorDoc(null);
    setSlotOptions([]);
    setPreferredSlotIds([]);
    setBusySlotIds([]);
    setTutorSalaryTouched(false);
    setCreateForm((prev) => ({
      ...prev,
      tutorId: "",
      finalisedActiveSlot: "",
      tutorSalary: "",
    }));
  }, [sortedTutorOptions, createForm.tutorId]);

  useEffect(() => {
    if (!createForm.parentId) {
      return;
    }

    if (createForm.mode !== "OFFLINE") {
      return;
    }

    const parent = parentOptions.find((item) => item?._id === createForm.parentId);
    if (!parent) {
      return;
    }

    setCreateForm((prev) => ({
      ...prev,
      address: parent.address || "",
    }));
  }, [createForm.parentId, createForm.mode, parentOptions]);

  useEffect(() => {
    const tutorId = createForm.tutorId || "";

    if (!tutorId) {
      setSlotOptions([]);
      setPreferredSlotIds([]);
      setBusySlotIds([]);
      setSelectedTutorDoc(null);
      setTutorSalaryTouched(false);
      setCreateForm((prev) => ({
        ...prev,
        tutorSalary: "",
        finalisedActiveSlot: "",
      }));
      return;
    }

    const fetchTutorSlots = async () => {
      setSlotLoading(true);
      try {
        const slotsReq = axios.get("/slots", {
          params: {
            page: 1,
            limit: 100,
            sort: "startTime",
            order: "asc",
          },
        });

        const activeSessionsReq = axios.get(`/classes/get/active/classesFor/tutor/${tutorId}`, {
          params: { status: "ACTIVE" },
        });

        const tutorPrefReq = axios.get("/tutor", {
          params: { id: tutorId },
        });

        const [slotsRes, activeSessionsRes, tutorPrefRes] = await Promise.all([
          slotsReq,
          activeSessionsReq,
          tutorPrefReq,
        ]);

        const slotsData = normalizeSlots(slotsRes);
        const normalizedSlots = Array.isArray(slotsData) ? slotsData : [];

        const sessionsArr = Array.isArray(activeSessionsRes?.data?.classes)
          ? activeSessionsRes.data.classes
          : Array.isArray(activeSessionsRes?.data?.data?.classes)
          ? activeSessionsRes.data.data.classes
          : Array.isArray(activeSessionsRes?.data?.data)
          ? activeSessionsRes.data.data
          : [];

        const busyIds = sessionsArr
          .map((session) => getSlotId(session?.finalisedActiveSlot))
          .filter(Boolean)
          .map((id) => String(id));

        setBusySlotIds([...new Set(busyIds)]);

        const tutorDoc =
          tutorPrefRes?.data?.data?.tutor ||
          tutorPrefRes?.data?.data ||
          tutorPrefRes?.data?.tutor ||
          null;

        setSelectedTutorDoc(tutorDoc);
        const preferredTeachingSlots = Array.isArray(tutorDoc?.preferredTeachingSlots)
          ? tutorDoc.preferredTeachingSlots
          : [];
        const preferredIds = preferredTeachingSlots
          .map((slot) => String(getSlotId(slot)))
          .filter(Boolean);
        setPreferredSlotIds([...new Set(preferredIds)]);

        const busySet = new Set(busyIds);
        const preferredSet = new Set(preferredIds);

        const availableSlots = normalizedSlots
          .filter((slot) => {
            const slotId = String(getSlotId(slot));
            if (!slotId) return false;
            if (busySet.has(slotId)) return false;
            if (preferredIds.length > 0) return preferredSet.has(slotId);
            return true;
          })
          .sort((a, b) => getSlotLabel(a).localeCompare(getSlotLabel(b)));

        setSlotOptions(availableSlots);
      } catch (error) {
        console.error("Error fetching tutor slots:", error);
        setSlotOptions([]);
        setPreferredSlotIds([]);
        setBusySlotIds([]);
        toast.error(
          error.response?.data?.message ||
            error.response?.data?.error ||
            error.message ||
            "Failed to load tutor slots"
        );
      } finally {
        setSlotLoading(false);
      }
    };

    fetchTutorSlots();
  }, [createForm.tutorId]);

  const getTutorHourlyRateForLevel = (tutorDoc, educationLevel) => {
    if (!tutorDoc) return 0;

    const pricingList = Array.isArray(tutorDoc?.levelBasedPricing)
      ? tutorDoc.levelBasedPricing
      : [];
    if (educationLevel) {
      const match = pricingList.find((pricing) => pricing?.level === educationLevel);
      const matchRate = Number(match?.hourlyRate || 0);
      if (Number.isFinite(matchRate) && matchRate > 0) {
        return matchRate;
      }
    }

    const fallbackRate = Number(tutorDoc?.hourlyRate || 0);
    return Number.isFinite(fallbackRate) ? fallbackRate : 0;
  };

  const computeTutorSalaryPerClass = ({ hourlyRate, duration }) => {
    const durationValue = Number(duration || 60);
    const sessionDurationInHours = durationValue > 24 ? durationValue / 60 : durationValue;
    const rateValue = Number(hourlyRate || 0);
    const rawSalary =
      (Number.isFinite(rateValue) ? rateValue : 0) *
      (Number.isFinite(sessionDurationInHours) ? sessionDurationInHours : 0);
    const normalizedSalary = Number.isFinite(rawSalary) ? Number(rawSalary.toFixed(2)) : 0;
    return normalizedSalary < 0 ? 0 : normalizedSalary;
  };

  useEffect(() => {
    if (!createDialogOpen) {
      return;
    }

    if (!createForm.tutorId || !selectedTutorDoc) {
      return;
    }

    if (tutorSalaryTouched) {
      return;
    }

    const selectedStudentIds = Array.isArray(createForm.students) ? createForm.students : [];
    const firstStudentId = selectedStudentIds[0];
    const firstStudent = studentOptions.find((student) => student?._id === firstStudentId);
    const educationLevel = getStudentEducationLevel(firstStudent);

    const hourlyRate = getTutorHourlyRateForLevel(selectedTutorDoc, educationLevel);
    const tutorSalaryValue = computeTutorSalaryPerClass({
      hourlyRate,
      duration: createForm.duration,
    });

    setCreateForm((prev) => ({
      ...prev,
      tutorSalary: tutorSalaryValue,
    }));
  }, [
    createDialogOpen,
    createForm.tutorId,
    createForm.students,
    createForm.duration,
    selectedTutorDoc,
    studentOptions,
    tutorSalaryTouched,
  ]);

  useEffect(() => {
    const totalClasses = resolveDefaultTotalClasses(
      createForm.totalNoOfClasses,
      createForm.numberOfClassesInAWeek
    );
    const paymentAmount = Number(createForm.paymentAmount || 0);
    const noOfMonths = Number(createForm.noOfMonths || 0);
    const classesPerWeek = Number(createForm.numberOfClassesInAWeek || 0);

    const packageSessions = resolvePackageSessionCount({
      totalClasses,
      noOfMonths,
      classesPerWeek,
    });
    const perSessionFee = packageSessions > 0 ? paymentAmount / packageSessions : 0;
    const monthlyFee =
      noOfMonths > 0 ? paymentAmount / noOfMonths : perSessionFee * classesPerWeek * 4;

    const normalizedPerSessionFee = Number(perSessionFee.toFixed(2));
    const normalizedMonthlyFee = Number(monthlyFee.toFixed(2));

    setCreateForm((prev) => {
      if (
        Number(prev.perSessionFee || 0) === normalizedPerSessionFee &&
        Number(prev.monthlyFee || 0) === normalizedMonthlyFee
      ) {
        return prev;
      }

      return {
        ...prev,
        perSessionFee: normalizedPerSessionFee,
        monthlyFee: normalizedMonthlyFee,
      };
    });
  }, [
    createForm.totalNoOfClasses,
    createForm.paymentAmount,
    createForm.noOfMonths,
    createForm.numberOfClassesInAWeek,
  ]);

  useEffect(() => {
    const parentMonthlyFee = Number(createForm.monthlyFee || 0);
    const tutorPerSessionFee = Number(createForm.tutorSalary || 0);
    const totalClasses = resolveDefaultTotalClasses(
      createForm.totalNoOfClasses,
      createForm.numberOfClassesInAWeek
    );
    const noOfMonths = Number(createForm.noOfMonths || 0);
    const classesPerWeek = Number(createForm.numberOfClassesInAWeek || 0);
    const monthlySessions = resolveMonthlySessionCount({
      totalClasses,
      noOfMonths,
      classesPerWeek,
    });
    const grossMargin = computeGrossMarginPercentage({
      parentFee: parentMonthlyFee,
      tutorFeePerSession: tutorPerSessionFee,
      monthlySessions,
    });
    const normalizedGrossMargin = Number(grossMargin.toFixed(2));

    setCreateForm((prev) => {
      if (Number(prev.grossMargin || 0) === normalizedGrossMargin) {
        return prev;
      }

      return {
        ...prev,
        grossMargin: normalizedGrossMargin,
      };
    });
  }, [
    createForm.monthlyFee,
    createForm.tutorSalary,
    createForm.totalNoOfClasses,
    createForm.noOfMonths,
    createForm.numberOfClassesInAWeek,
  ]);

  const fetchNormalClasses = async () => {
    try {
      setLoading(true);
      // Fetch normal classes using the new endpoint
      const response = await axios.get("/classes/sessions/all", {
        params: {
          page: 1,
          limit: 100,
          status: "CREATED,SCHEDULED,ONGOING,ON_HOLD,COMPLETED,DISCONTINUED",
        },
      });

      if (response.data.success) {
        const sessions = response.data.data || [];
        setClassData(sessions);

        // Calculate counts based on status and class type
        const newCounts = {
          all: sessions.length,
          active: sessions.filter(
            (s) => s.status === "ONGOING" || s.status === "CREATED" || s.status === "SCHEDULED"
          ).length,
          hold: sessions.filter((s) => s.status === "ON_HOLD").length,
          completed: sessions.filter((s) => s.status === "COMPLETED").length,
          discontinued: sessions.filter((s) => s.status === "DISCONTINUED").length,
          sme: sessions.filter((s) => s.classType === "SME").length,
          // Combined: Multiple students in one class (COMBINED classType OR more than 1 student)
          combined: sessions.filter(
            (s) =>
              s.classType === "COMBINED" ||
              (s.students && Array.isArray(s.students) && s.students.length > 1)
          ).length,
          // Separate: Single student per class (exactly 1 student)
          separate: sessions.filter(
            (s) => s.students && Array.isArray(s.students) && s.students.length === 1
          ).length,
        };
        setCounts(newCounts);
      }
    } catch (error) {
      console.error("Error fetching normal classes:", error);
    } finally {
      setLoading(false);
    }
  };

  // Filter data based on active tab
  const getFilteredData = () => {
    let filtered = classData;

    // Filter by tab
    switch (activeTab) {
      case "Active":
        filtered = filtered.filter(
          (s) => s.status === "ONGOING" || s.status === "CREATED" || s.status === "SCHEDULED"
        );
        break;
      case "Hold":
        filtered = filtered.filter((s) => s.status === "ON_HOLD");
        break;
      case "Completed":
        filtered = filtered.filter((s) => s.status === "COMPLETED");
        break;
      case "Discontinued":
        filtered = filtered.filter((s) => s.status === "DISCONTINUED");
        break;
      case "SME":
        filtered = filtered.filter((s) => s.classType === "SME");
        break;
      case "Combined":
        // Combined: Multiple students in one class
        filtered = filtered.filter(
          (s) =>
            s.classType === "COMBINED" ||
            (s.students && Array.isArray(s.students) && s.students.length > 1)
        );
        break;
      case "Separate":
        // Separate: Single student per class (exactly 1 student)
        filtered = filtered.filter(
          (s) => s.students && Array.isArray(s.students) && s.students.length === 1
        );
        break;
      default:
        break;
    }

    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter((item) => {
        const studentName = item.student?.name?.toLowerCase() || "";
        const tutorName = item.tutor?.name?.toLowerCase() || "";
        const parentName =
          item.parentId?.fullName?.toLowerCase() || item.parentId?.name?.toLowerCase() || "";
        return (
          studentName.includes(searchQuery.toLowerCase()) ||
          tutorName.includes(searchQuery.toLowerCase()) ||
          parentName.includes(searchQuery.toLowerCase())
        );
      });
    }

    return filtered;
  };

  // Get filtered data
  const filteredData = getFilteredData();

  // Pagination
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedData = filteredData.slice(startIndex, endIndex);

  const { columns, rows } = ClassMgtTableData(handleDeleteClick, paginatedData, startIndex);

  const tabsData = [
    { label: "All", count: counts.all, color: "#2027e1ff", content: "" },
    { label: "Active", count: counts.active, color: "#ec3c10ff", content: "" },
    { label: "Hold", count: counts.hold, color: "#ec10c7ff", content: "" },
    { label: "Completed", count: counts.completed, color: "#10a6ecff", content: "" },
    { label: "Discontinued", count: counts.discontinued, color: "#1a6808b7", content: "" },
    { label: "SME", count: counts.sme, color: "#1a6808b7", content: "" },
    { label: "Combined", count: counts.combined, color: "#1a6808b7", content: "" },
    { label: "Separate", count: counts.separate, color: "#1a6808b7", content: "" },
  ];

  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    setCurrentPage(1); // Reset to first page when changing tab
  };

  // Pagination handlers
  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(event.target.value);
    setCurrentPage(1); // Reset to first page when changing rows per page
  };

  // Search handler
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleOpenCreateDialog = () => {
    setCreateForm(initialCreateForm);
    setStudentOptions([]);
    setSlotOptions([]);
    setPreferredSlotIds([]);
    setBusySlotIds([]);
    setSelectedTutorDoc(null);
    setTutorSalaryTouched(false);
    setCreateDialogOpen(true);
  };

  const handleCloseCreateDialog = () => {
    if (createLoading) {
      return;
    }
    setCreateDialogOpen(false);
    setCreateForm(initialCreateForm);
    setStudentOptions([]);
    setSelectedTutorDoc(null);
    setTutorSalaryTouched(false);
  };

  const handleCreateFormChange = (field, value) => {
    setCreateForm((prev) => {
      const nextForm = {
        ...prev,
        [field]: value,
      };

      if (field === "numberOfClassesInAWeek") {
        nextForm.totalNoOfClasses = resolveDefaultTotalClasses(
          prev.totalNoOfClasses,
          value
        );
      }

      return nextForm;
    });
  };

  const totalCycles = Math.max(0, Math.floor(Number(createForm.noOfMonths || 0)));
  const completedCyclesValue = Math.max(
    0,
    Math.min(totalCycles, Math.floor(Number(createForm.completedCycles || 0)))
  );
  const currentCycleLabel =
    totalCycles === 0
      ? "-"
      : completedCyclesValue >= totalCycles
      ? "Completed"
      : `Cycle ${completedCyclesValue + 1}`;

  useEffect(() => {
    if (createForm.mode === "ONLINE") {
      setCreateForm((prev) => ({
        ...prev,
        address: "",
      }));
    }

    if (createForm.mode === "OFFLINE") {
      setCreateForm((prev) => ({
        ...prev,
        meetingLink: "",
      }));
    }
  }, [createForm.mode]);

  useEffect(() => {
    const normalizedCompletedCycles = Math.max(
      0,
      Math.min(totalCycles, Math.floor(Number(createForm.completedCycles || 0)))
    );

    if (normalizedCompletedCycles !== Number(createForm.completedCycles || 0)) {
      setCreateForm((prev) => ({
        ...prev,
        completedCycles: normalizedCompletedCycles,
      }));
    }
  }, [createForm.noOfMonths, totalCycles, createForm.completedCycles]);

  const getStudentClassName = (student) =>
    student?.class?.name || student?.className || student?.class || "";

  const getSelectedClassLabel = () => {
    const selectedIds = Array.isArray(createForm.students) ? createForm.students : [];
    if (selectedIds.length === 0) return "";

    const classNames = selectedIds
      .map((id) => studentOptions.find((student) => student?._id === id))
      .map((student) => getStudentClassName(student))
      .filter(Boolean);

    const uniqueClasses = Array.from(new Set(classNames));
    if (uniqueClasses.length === 0) return "";
    if (uniqueClasses.length === 1) return uniqueClasses[0];
    return "Multiple Classes";
  };

  const handleCreateClassSession = async () => {
    if (!createForm.parentId) {
      toast.error("Parent is required");
      return;
    }

    if (!createForm.students || createForm.students.length === 0) {
      toast.error("At least one student is required");
      return;
    }

    if (!createForm.paymentAmount || Number(createForm.paymentAmount) < 0) {
      toast.error("Please enter valid tuition fee");
      return;
    }

    try {
      setCreateLoading(true);
      const resolvedTotalNoOfClasses = resolveDefaultTotalClasses(
        createForm.totalNoOfClasses,
        createForm.numberOfClassesInAWeek
      );
      const resolvedPackageSessionCount = resolvePackageSessionCount({
        totalClasses: resolvedTotalNoOfClasses,
        noOfMonths: createForm.noOfMonths,
        classesPerWeek: createForm.numberOfClassesInAWeek,
      });

      const payload = {
        parentId: createForm.parentId,
        students: createForm.students,
        tutorId: createForm.tutorId || null,
        mode: createForm.mode,
        classType: createForm.classType,
        totalNoOfClasses: resolvedPackageSessionCount,
        numberOfClassesInAWeek: Number(createForm.numberOfClassesInAWeek),
        paymentAmount: Number(createForm.paymentAmount),
        paymentMethod: createForm.paymentMethod,
        perSessionFee: createForm.perSessionFee ? Number(createForm.perSessionFee) : 0,
        monthlyFee: createForm.monthlyFee ? Number(createForm.monthlyFee) : 0,
        tutorSalary: createForm.tutorSalary !== "" ? Number(createForm.tutorSalary) : undefined,
        grossMargin: Number(createForm.grossMargin || 0),
        noOfMonths: createForm.noOfMonths ? Number(createForm.noOfMonths) : 1,
        completedCycles: completedCyclesValue,
        duration: createForm.duration ? Number(createForm.duration) : 60,
        address: showAddress ? createForm.address : "",
        scheduleDate: createForm.scheduleDate || null,
        meetingDetails:
          showMeetingLink && createForm.meetingLink
            ? { meetingLink: createForm.meetingLink }
            : undefined,
        finalisedActiveSlot: createForm.finalisedActiveSlot || null,
        preferedTimeSlots: createForm.finalisedActiveSlot ? [createForm.finalisedActiveSlot] : [],
        status: createForm.status,
      };

      const response = await axios.post("/classes/session", payload);

      if (response?.data?.success) {
        toast.success(response?.data?.message || "Class created successfully");
        handleCloseCreateDialog();
        fetchNormalClasses();
      }
    } catch (error) {
      console.error("Error creating class session:", error);
      toast.error(error?.response?.data?.message || "Failed to create class");
    } finally {
      setCreateLoading(false);
    }
  };

  const isGroupClass = Array.isArray(createForm.students) && createForm.students.length > 1;
  const showMeetingLink = createForm.mode === "ONLINE" || createForm.mode === "BOTH";
  const showAddress = createForm.mode === "OFFLINE" || createForm.mode === "BOTH";

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <ArgonBox py={3}>
        <ArgonBox mb={3}>
          <Card>
            <ArgonBox p={3}>
              <ArgonBox display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <ArgonTypography variant="h6" fontWeight="medium">
                  All Classes
                </ArgonTypography>
                <ArgonButton
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={handleOpenCreateDialog}
                >
                  Create Class
                </ArgonButton>
              </ArgonBox>

              <ArgonBox display="flex" alignItems="center" justifyContent="space-between">
                <ArgonBox sx={{ height: "36px" }}>
                  <ApnaTabs tabsData={tabsData} onTabChange={handleTabChange} />
                </ArgonBox>
                <ArgonBox
                  sx={{ height: "36px" }}
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                >
                  <ArgonInput
                    placeholder="search"
                    name="search"
                    size="small"
                    type="text"
                    variant="outlined"
                    sx={{ margin: "10px" }}
                    value={searchQuery}
                    onChange={handleSearchChange}
                  />
                </ArgonBox>
              </ArgonBox>
            </ArgonBox>

            <ArgonBox
              sx={{
                "& .MuiTableRow-root:not(:last-child)": {
                  "& td": {
                    borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                      `${borderWidth[1]} solid ${borderColor}`,
                  },
                },
              }}
            >
              {loading ? (
                <ArgonBox display="flex" justifyContent="center" alignItems="center" p={5}>
                  <CircularProgress color="primary" />
                </ArgonBox>
              ) : filteredData.length === 0 ? (
                <ArgonBox textAlign="center" py={8}>
                  <ConfirmationNumber sx={{ fontSize: 80, color: "#cbd5e1", mb: 2 }} />
                  <ArgonTypography variant="h5" color="#64748b" fontWeight="bold" mb={1}>
                    No Classes Found
                  </ArgonTypography>
                  <ArgonTypography variant="body2" color="#94a3b8">
                    Try adjusting the filters or check back later.
                  </ArgonTypography>
                </ArgonBox>
              ) : (
                <Table columns={columns} rows={rows} />
              )}
            </ArgonBox>

            {/* Pagination Controls */}
            {!loading && filteredData.length > 0 && (
              <PaginationBar
                currentPage={currentPage}
                rowsPerPage={rowsPerPage}
                totalItems={filteredData.length}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
              />
            )}
          </Card>
        </ArgonBox>

        {/* dialog popup */}

        <DeleteDialog
          open={deleteDialog}
          onClose={handleCloseDialog}
          onConfirm={handleCloseDialog}
          title="Delete Class"
          description="Are you sure you want to delete this class?"
          confirmText="Delete Class"
          cancelText="Cancel"
        />

        <Dialog open={createDialogOpen} onClose={handleCloseCreateDialog} maxWidth="md" fullWidth>
          <ArgonBox sx={{ backgroundColor: "#f8f9fc" }}>
            <DialogTitle
              sx={{
                backgroundColor: "transparent",
                pb: 1,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <ArgonTypography variant="h5" fontWeight="bold">
                Create New Class
              </ArgonTypography>
              <IconButton
                onClick={handleCloseCreateDialog}
                disabled={createLoading}
                sx={{ color: "text.secondary" }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <Divider sx={{ my: 0 }} />
            <DialogContent sx={{ p: 3, backgroundColor: "transparent", borderBottom: "none" }}>
              {/* Basic Information Card */}
              <Card
                sx={{
                  backgroundColor: "white",
                  borderRadius: "10px",
                  p: 3,
                  mb: 3,
                  boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
                  border: "1px solid #f5f5f5",
                }}
              >
                <ArgonBox display="flex" alignItems="center" gap={2} mb={3}>
                  <ArgonBox
                    sx={{
                      background: "linear-gradient(45deg, #ff956f, #F7931E)",
                      borderRadius: "10px",
                      p: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 2px 8px rgba(255, 107, 53, 0.25)",
                    }}
                  >
                    <Person sx={{ color: "white", fontSize: "20px" }} />
                  </ArgonBox>
                  <ArgonBox>
                    <ArgonTypography variant="h6" fontWeight="600" color="#2c3e50">
                      Basic Information
                    </ArgonTypography>
                    <ArgonTypography variant="body2" color="#6c757d">
                      Parent, students, and tutor mapping
                    </ArgonTypography>
                  </ArgonBox>
                </ArgonBox>

                <Grid container spacing={3} alignItems="flex-end">
                  <Grid item xs={12} md={6}>
                    <ArgonTypography variant="body2" fontWeight="600" mb={1}>
                      Parent <span style={{ color: "red" }}>*</span>
                    </ArgonTypography>
                    <Dropdown
                      label=""
                      value={createForm.parentId || ""}
                      onChange={(e) => {
                        const nextParentId = e.target.value;
                        handleCreateFormChange("parentId", nextParentId);
                        handleCreateFormChange("students", []);

                        if (createForm.mode === "OFFLINE") {
                          const parent = parentOptions.find((item) => item?._id === nextParentId);
                          handleCreateFormChange("address", parent?.address || "");
                        }
                      }}
                      options={[
                        {
                          label: "Select Parent",
                          value: "",
                          disabled: optionLoading || createLoading,
                        },
                        ...parentOptions.map((parent) => ({
                          label: parent.fullName || parent.name || "Unnamed Parent",
                          value: parent._id,
                        })),
                      ]}
                      width="100%"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <ArgonTypography variant="body2" fontWeight="600" mb={1}>
                      Students <span style={{ color: "red" }}>*</span>
                    </ArgonTypography>
                    <FormControl fullWidth>
                      <Select
                        multiple
                        value={createForm.students}
                        onChange={(e) => handleCreateFormChange("students", e.target.value)}
                        disabled={!createForm.parentId || optionLoading || createLoading}
                        displayEmpty
                        renderValue={(selected) => {
                          if (selected.length === 0)
                            return <span style={{ color: "#7b7a7aff" }}>Select Students</span>;
                          return studentOptions
                            .filter((student) => selected.includes(student._id))
                            .map((student) => student.name)
                            .join(", ");
                        }}
                        sx={{
                          minHeight: "40px",
                          padding: "4px 8px",
                          borderRadius: "8px",
                          border: "1px solid #d2d6da",
                          fontSize: "14px",
                          fontWeight: "400",
                          color: "#495057",
                          backgroundColor: "#fff",
                          "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                          "&.Mui-disabled": {
                            backgroundColor: "#e9ecef",
                          },
                        }}
                      >
                        {studentOptions.length === 0 ? (
                          <MenuItem disabled value="">
                            No Students Found
                          </MenuItem>
                        ) : (
                          studentOptions.map((student) => (
                            <MenuItem key={student._id} value={student._id}>
                              {student.name}
                            </MenuItem>
                          ))
                        )}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <ArgonTypography variant="body2" fontWeight="600" mb={1}>
                      Class
                    </ArgonTypography>
                    <ArgonInput
                      fullWidth
                      value={getSelectedClassLabel()}
                      placeholder="Select student to auto-fill class"
                      disabled
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <ArgonTypography variant="body2" fontWeight="600" mb={1}>
                      Tutor
                    </ArgonTypography>
                    <Dropdown
                      label=""
                      value={createForm.tutorId || ""}
                      onChange={(e) => {
                        const nextTutorId = e.target.value;
                        setTutorSalaryTouched(false);
                        handleCreateFormChange("tutorId", nextTutorId);
                        handleCreateFormChange("finalisedActiveSlot", "");
                      }}
                      options={[
                        {
                          label: "Select Tutor",
                          value: "",
                          disabled: optionLoading || createLoading,
                        },
                        ...sortedTutorOptions.map((tutor) => ({
                          label: formatTutorLabel(tutor, tutor.distanceKm),
                          value: tutor.id || tutor._id,
                        })),
                      ]}
                      width="100%"
                    />
                  </Grid>
                </Grid>
              </Card>

              {/* Class Settings Card */}
              <Card
                sx={{
                  backgroundColor: "white",
                  borderRadius: "10px",
                  p: 3,
                  mb: 3,
                  boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
                  border: "1px solid #f5f5f5",
                }}
              >
                <ArgonBox display="flex" alignItems="center" gap={2} mb={3}>
                  <ArgonBox
                    sx={{
                      background: "linear-gradient(45deg, #4ECDC4, #45B7D1)",
                      borderRadius: "10px",
                      p: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 2px 8px rgba(78, 205, 196, 0.25)",
                    }}
                  >
                    <Settings sx={{ color: "white", fontSize: "20px" }} />
                  </ArgonBox>
                  <ArgonBox>
                    <ArgonTypography variant="h6" fontWeight="600" color="#2c3e50">
                      Class Settings
                    </ArgonTypography>
                    <ArgonTypography variant="body2" color="#6c757d">
                      Configuration for mode, type, and duration
                    </ArgonTypography>
                  </ArgonBox>
                </ArgonBox>

                <Grid container spacing={3} alignItems="flex-end">
                  <Grid item xs={12} md={4}>
                    <ArgonTypography variant="body2" fontWeight="600" mb={1}>
                      Mode
                    </ArgonTypography>
                    <Dropdown
                      label=""
                      value={createForm.mode}
                      onChange={(e) => handleCreateFormChange("mode", e.target.value)}
                      options={[
                        { label: "OFFLINE", value: "OFFLINE" },
                        { label: "ONLINE", value: "ONLINE" },
                        { label: "BOTH", value: "BOTH" },
                      ]}
                      width="100%"
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <ArgonTypography variant="body2" fontWeight="600" mb={1}>
                      Class Type
                    </ArgonTypography>
                    <Dropdown
                      label=""
                      value={createForm.classType}
                      onChange={(e) => handleCreateFormChange("classType", e.target.value)}
                      options={
                        isGroupClass
                          ? [{ label: "GROUP", value: "GROUP" }]
                          : [{ label: "NORMAL", value: "NORMAL" }]
                      }
                      width="100%"
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <ArgonTypography variant="body2" fontWeight="600" mb={1}>
                      Status
                    </ArgonTypography>
                    <Dropdown
                      label=""
                      value={createForm.status}
                      onChange={(e) => handleCreateFormChange("status", e.target.value)}
                      options={[
                        { label: "CREATED", value: "CREATED" },
                        { label: "SCHEDULED", value: "SCHEDULED" },
                        { label: "ONGOING", value: "ONGOING" },
                        { label: "ON_HOLD", value: "ON_HOLD" },
                      ]}
                      width="100%"
                    />
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <ArgonTypography variant="body2" fontWeight="600" mb={1}>
                      Total No. of Classes (Package Wise)<span style={{ color: "red" }}>*</span>
                    </ArgonTypography>
                    <ArgonInput
                      type="number"
                      fullWidth
                      inputProps={{ min: 1 }}
                      value={createForm.totalNoOfClasses}
                      onChange={(e) => handleCreateFormChange("totalNoOfClasses", e.target.value)}
                      disabled={createLoading}
                    />
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <ArgonTypography variant="body2" fontWeight="600" mb={1}>
                      Classes / Week <span style={{ color: "red" }}>*</span>
                    </ArgonTypography>
                    <ArgonInput
                      type="number"
                      fullWidth
                      inputProps={{ min: 1, max: 7 }}
                      value={createForm.numberOfClassesInAWeek}
                      onChange={(e) =>
                        handleCreateFormChange("numberOfClassesInAWeek", e.target.value)
                      }
                      disabled={createLoading}
                    />
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <ArgonTypography variant="body2" fontWeight="600" mb={1}>
                      No. of Months
                    </ArgonTypography>
                    <ArgonInput
                      type="number"
                      fullWidth
                      inputProps={{ min: 1 }}
                      value={createForm.noOfMonths}
                      onChange={(e) => handleCreateFormChange("noOfMonths", e.target.value)}
                      disabled={createLoading}
                    />
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <ArgonTypography variant="body2" fontWeight="600" mb={1}>
                      Duration (mins)
                    </ArgonTypography>
                    <ArgonInput
                      type="number"
                      fullWidth
                      inputProps={{ min: 0 }}
                      value={createForm.duration}
                      onChange={(e) => handleCreateFormChange("duration", e.target.value)}
                      disabled={createLoading}
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <ArgonTypography variant="body2" fontWeight="600" mb={1}>
                      Total Cycles
                    </ArgonTypography>
                    <ArgonInput type="number" fullWidth value={totalCycles} disabled />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <ArgonTypography variant="body2" fontWeight="600" mb={1}>
                      Completed Cycles
                    </ArgonTypography>
                    <ArgonInput
                      type="number"
                      fullWidth
                      inputProps={{ min: 0, max: totalCycles }}
                      value={createForm.completedCycles}
                      onChange={(e) => handleCreateFormChange("completedCycles", e.target.value)}
                      disabled={createLoading || totalCycles === 0}
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <ArgonTypography variant="body2" fontWeight="600" mb={1}>
                      Current Cycle
                    </ArgonTypography>
                    <ArgonInput fullWidth value={currentCycleLabel} disabled />
                  </Grid>
                </Grid>
              </Card>

              {/* Fees & Schedule Card */}
              <Card
                sx={{
                  backgroundColor: "white",
                  borderRadius: "10px",
                  p: 3,
                  boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
                  border: "1px solid #f5f5f5",
                }}
              >
                <ArgonBox display="flex" alignItems="center" gap={2} mb={3}>
                  <ArgonBox
                    sx={{
                      background: "linear-gradient(45deg, #96CEB4, #88D8C0)",
                      borderRadius: "10px",
                      p: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 2px 8px rgba(150, 206, 180, 0.25)",
                    }}
                  >
                    <Payment sx={{ color: "white", fontSize: "20px" }} />
                  </ArgonBox>
                  <ArgonBox>
                    <ArgonTypography variant="h6" fontWeight="600" color="#2c3e50">
                      Fees & Schedule
                    </ArgonTypography>
                    <ArgonTypography variant="body2" color="#6c757d">
                      Financial details and initial start date
                    </ArgonTypography>
                  </ArgonBox>
                </ArgonBox>

                <Grid container spacing={3} alignItems="flex-end">
                  <Grid item xs={12} md={3}>
                    <ArgonTypography variant="body2" fontWeight="600" mb={1}>
                      Total fee charged <span style={{ color: "red" }}>*</span>
                    </ArgonTypography>
                    <ArgonInput
                      type="number"
                      fullWidth
                      placeholder="Total Fee"
                      inputProps={{ min: 0 }}
                      value={createForm.paymentAmount}
                      onChange={(e) => handleCreateFormChange("paymentAmount", e.target.value)}
                      disabled={createLoading}
                    />
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <ArgonTypography variant="body2" fontWeight="600" mb={1}>
                      Payment Mode
                    </ArgonTypography>
                    <Dropdown
                      label=""
                      value={createForm.paymentMethod}
                      onChange={(e) => handleCreateFormChange("paymentMethod", e.target.value)}
                      options={[
                        { label: "CASH", value: "CASH" },
                        { label: "ONLINE", value: "ONLINE" },
                      ]}
                      width="100%"
                    />
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <ArgonTypography variant="body2" fontWeight="600" mb={1}>
                      Per Session Fee (P)
                    </ArgonTypography>
                    <ArgonInput
                      type="number"
                      fullWidth
                      placeholder="Per Session Fee"
                      inputProps={{ min: 0 }}
                      value={createForm.perSessionFee}
                    />
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <ArgonTypography variant="body2" fontWeight="600" mb={1}>
                      Per Session Fee (T)
                    </ArgonTypography>
                    <ArgonInput
                      type="number"
                      fullWidth
                      placeholder="Tutor per session fee"
                      inputProps={{ min: 0 }}
                      value={createForm.tutorSalary}
                      onChange={(e) => {
                        setTutorSalaryTouched(true);
                        handleCreateFormChange("tutorSalary", e.target.value);
                      }}
                      disabled={!createForm.tutorId || createLoading}
                    />
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <ArgonTypography variant="body2" fontWeight="600" mb={1}>
                      Monthly Fee
                    </ArgonTypography>
                    <ArgonInput
                      type="number"
                      fullWidth
                      placeholder="Monthly Fee"
                      inputProps={{ min: 0 }}
                      value={createForm.monthlyFee}
                    />
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <ArgonTypography variant="body2" fontWeight="600" mb={1}>
                      Gross Margin (GM)
                    </ArgonTypography>
                    <ArgonInput
                      type="text"
                      fullWidth
                      value={`${createForm.grossMargin || 0}%`}
                      disabled
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <ArgonTypography variant="body2" fontWeight="600" mb={1}>
                      Start Date
                    </ArgonTypography>
                    <ArgonInput
                      type="date"
                      fullWidth
                      value={createForm.scheduleDate}
                      onChange={(e) => handleCreateFormChange("scheduleDate", e.target.value)}
                      disabled={createLoading}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <ArgonTypography variant="body2" fontWeight="600" mb={1}>
                      Slot
                    </ArgonTypography>
                    <Dropdown
                      label=""
                      value={createForm.finalisedActiveSlot || ""}
                      onChange={(e) =>
                        handleCreateFormChange("finalisedActiveSlot", e.target.value)
                      }
                      options={[
                        {
                          label: slotLoading
                            ? "Loading slots..."
                            : createForm.tutorId
                            ? "Select Slot"
                            : "Select Tutor First",
                          value: "",
                          disabled: true,
                        },
                        ...slotOptions.map((slot) => ({
                          label: getSlotLabel(slot) || "Slot",
                          value: getSlotId(slot),
                        })),
                      ]}
                      width="100%"
                    />
                  </Grid>

                  {showMeetingLink && (
                    <Grid item xs={12} md={6}>
                      <ArgonTypography variant="body2" fontWeight="600" mb={1}>
                        Meeting Link
                      </ArgonTypography>
                      <ArgonInput
                        fullWidth
                        placeholder="Paste meeting link"
                        value={createForm.meetingLink}
                        onChange={(e) => handleCreateFormChange("meetingLink", e.target.value)}
                        disabled={createLoading}
                      />
                    </Grid>
                  )}

                  {showAddress && (
                    <Grid item xs={12} md={6}>
                      <ArgonTypography variant="body2" fontWeight="600" mb={1}>
                        Address
                      </ArgonTypography>
                      <ArgonInput
                        fullWidth
                        placeholder="Class location"
                        value={createForm.address}
                        onChange={(e) => handleCreateFormChange("address", e.target.value)}
                        disabled={createLoading}
                      />
                    </Grid>
                  )}
                </Grid>
              </Card>
            </DialogContent>
            {/* <Divider sx={{ my: 0 }} /> */}
            <DialogActions
              sx={{ px: 3, py: 2, backgroundColor: "transparent", borderRadius: "0 0 10px 10px" }}
            >
              <ArgonButton
                variant="outlined"
                color="secondary"
                onClick={handleCloseCreateDialog}
                disabled={createLoading}
              >
                Cancel
              </ArgonButton>
              <ArgonButton
                variant="gradient"
                color="info"
                onClick={handleCreateClassSession}
                disabled={createLoading || optionLoading}
              >
                {createLoading ? "Creating..." : "Create Class"}
              </ArgonButton>
            </DialogActions>
          </ArgonBox>
        </Dialog>
      </ArgonBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Tables;
